<template>
	<div class="hero-section relative flex flex-col overflow-hidden px-6 py-20 min-h-dvh-safe">
		<!-- Animated gradient background -->
		<div class="absolute inset-0 bg-gradient-to-b from-indigo-50 via-blue-50 to-white opacity-60"></div>

		<!-- <ClientOnly>
			<ThreeParticleBackground />
		</ClientOnly> -->

		<LandingShape3
			class="shape-float absolute end-[-39px] top-[-199px] z-[1] h-[271px] w-[271px] transition-transform duration-300"
		/>
		<LandingShape8
			class="absolute start-0 top-3 z-[1] h-auto w-[57px] transition-transform duration-300 rtl:-scale-x-100"
		/>
		<LandingShape9
			class="absolute start-0 top-[343px] z-[1] h-auto w-[57px] text-[#E4EFF8] transition-transform duration-300 ltr:-scale-x-100"
		/>

		<div
			class="relative z-[1] mt-12 grid w-full max-w-screen-xl flex-grow grid-cols-1 gap-x-12 gap-y-16 self-center max-lg:mb-10 md:mt-20 lg:mt-0 lg:grid-cols-12 lg:gap-y-32"
		>
			<div class="col-span-1 flex flex-col items-center justify-center max-lg:order-2 lg:col-span-6">
				<div
					ref="heroTextContent"
					class="hero-text-content relative flex max-w-[450px] flex-col"
					:class="{ 'animate-in': isTextVisible }"
				>
					<LandingShape2 class="shape-pulse absolute start-[-30px] top-[-80px] h-auto w-11" />
					<LandingShape4
						class="shape-pulse-delayed absolute end-[-70px] top-[-20px] h-auto w-11 text-[#F3CDB6]"
					/>
					<LandingShape5 class="shape-pulse absolute bottom-[-110px] start-[-60px] h-auto w-11" />
					<h1
						class="hero-title mb-8 text-3xl font-black leading-normal text-[#382C5C] max-lg:text-center md:text-4xl lg:text-5xl"
					>
						{{ $t("app_name") + " - " }}
						<I18nT scope="global" keypath="top_section_title">
							<template #hr>
								<span class="gradient-text">
									{{ $t("human_resource") }}
								</span>
							</template>
						</I18nT>
					</h1>
					<NuxtLink
						v-if="!user?.id"
						:to="$routeUrl.LoginUrl()"
						class="hero-cta ripple-bg-primary text-white self-center rounded-lg px-10 py-4 text-sm font-bold shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl lg:self-start"
					>
						{{ $t("login_register") }}
					</NuxtLink>
					<NuxtLink
						v-else
						:to="$routeUrl.PanelDashboardUrl()"
						class="hero-cta ripple-bg-primary text-white self-center rounded-lg px-10 py-4 text-sm font-bold shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl lg:self-start"
					>
						{{ $t("dashboard") }}
					</NuxtLink>
				</div>
			</div>
			<div class="col-span-1 flex flex-col items-center justify-center max-lg:px-4 lg:col-span-6">
				<div
					ref="heroImages"
					class="hero-images perspective-1000 relative flex flex-col"
					:class="{ 'animate-in': isImagesVisible }"
				>
					<img
						ref="heroBgImage"
						src="/img/landing_top_bg.png"
						alt=""
						class="hero-bg-image absolute left-0 top-0 h-full w-full translate-x-[2.5%] translate-y-[5%] scale-[1.15] object-contain transition-transform duration-300 ease-out"
					/>
					<img
						ref="heroMainImage"
						src="/img/landing_top.png"
						alt=""
						class="hero-main-image relative aspect-square w-full max-w-[500px] object-contain transition-transform duration-300 ease-out"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useMouse, useElementVisibility } from "@vueuse/core"
import LandingShape2 from "~/assets/svg/landing_shape_2.svg"
import LandingShape3 from "~/assets/svg/landing_shape_3.svg"
import LandingShape4 from "~/assets/svg/landing_shape_4.svg"
import LandingShape5 from "~/assets/svg/landing_shape_5.svg"
import LandingShape8 from "~/assets/svg/landing_shape_8.svg"
import LandingShape9 from "~/assets/svg/landing_shape_9.svg"

const userStore = useUserStore()
const user = computed(() => userStore.user)

// Mouse tracking for parallax effect using VueUse
const heroMainImage = ref<HTMLImageElement | null>(null)
const heroBgImage = ref<HTMLImageElement | null>(null)
const heroTextContent = ref<HTMLElement | null>(null)
const heroImages = ref<HTMLElement | null>(null)

const { x, y } = useMouse()

// Intersection observers for scroll animations
const isTextVisible = useElementVisibility(heroTextContent)
const isImagesVisible = useElementVisibility(heroImages)

// Computed parallax transforms
const mainImageTransform = computed(() => {
	if (!heroMainImage.value) return ""

	const centerX = window.innerWidth / 2
	const centerY = window.innerHeight / 2

	const deltaX = (x.value - centerX) / centerX
	const deltaY = (y.value - centerY) / centerY

	// Very subtle parallax effect - minimal movement
	const mainTranslateX = deltaX * 5
	const mainTranslateY = deltaY * 5
	const mainRotateY = deltaX * 2
	const mainRotateX = -deltaY * 2

	return `translate(${mainTranslateX}px, ${mainTranslateY}px) rotateY(${mainRotateY}deg) rotateX(${mainRotateX}deg)`
})

const bgImageTransform = computed(() => {
	if (!heroBgImage.value) return "translate(2.5%, 5%) scale(1.15)"

	const centerX = window.innerWidth / 2
	const centerY = window.innerHeight / 2

	const deltaX = (x.value - centerX) / centerX
	const deltaY = (y.value - centerY) / centerY

	// Background moves extremely slightly for depth effect
	const bgTranslateX = deltaX * 1.5 + 2.5
	const bgTranslateY = deltaY * 1.5 + 5

	return `translate(${bgTranslateX}%, ${bgTranslateY}%) scale(1.15)`
})

// Apply transforms reactively
watch([mainImageTransform, bgImageTransform], ([mainTransform, bgTransform]) => {
	if (heroMainImage.value) {
		heroMainImage.value.style.transform = mainTransform
	}
	if (heroBgImage.value) {
		heroBgImage.value.style.transform = bgTransform
	}
})
</script>

<style scoped>
/* Gradient text */
.gradient-text {
	background: linear-gradient(135deg, #ea6939 0%, #ea3a86 100%);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
}

/* Floating shapes animations */
@keyframes float {
	0%,
	100% {
		transform: translateY(0px);
	}
	50% {
		transform: translateY(-20px);
	}
}

.shape-float {
	animation: float 6s ease-in-out infinite;
}

/* Pulse animations for small shapes */
@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
		opacity: 1;
	}
	50% {
		transform: scale(1.1);
		opacity: 0.8;
	}
}

@keyframes pulse-delayed {
	0%,
	100% {
		transform: scale(1);
		opacity: 1;
	}
	50% {
		transform: scale(1.15);
		opacity: 0.7;
	}
}

.shape-pulse {
	animation: pulse 3s ease-in-out infinite;
}

.shape-pulse-delayed {
	animation: pulse-delayed 4s ease-in-out infinite;
}

/* Hero section animations */
.hero-text-content,
.hero-images {
	opacity: 0;
	transform: translateY(30px);
	transition:
		opacity 0.8s ease-out,
		transform 0.8s ease-out;
}

.hero-text-content.animate-in {
	opacity: 1;
	transform: translateY(0);
}

.hero-images.animate-in {
	opacity: 1;
	transform: translateY(0);
	transition-delay: 0.2s;
}

/* Hero title animation */
.hero-title {
	animation: fadeInUp 1s ease-out;
}

@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Hero CTA animation */
.hero-cta {
	animation: fadeInUp 1s ease-out 0.3s both;
}

/* 3D perspective for images */
.perspective-1000 {
	perspective: 1000px;
}

.hero-main-image {
	transform-style: preserve-3d;
	will-change: transform;
}

.hero-bg-image {
	will-change: transform;
}

/* Smooth transitions on hover */
.hero-section :deep(svg) {
	transition: transform 0.3s ease-out;
}
</style>
